import CustomTable from "components/common/CustomTable";
import CustomToast from "components/common/CustomToast";
import EmptyStateComp from "components/common/EmptyStateComp";

import SynthTooltip from "components/common/SynthTooltip";
import { formatMoney } from "helpers";

import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import ResponsivePaginationComponent from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic.css";
import { dashboardActions } from "redux/Ldb/actions";
import useGetBundleDashboardGWMTooltip from "hooks/Dashboard/useGetBundleDashboardGWMTooltip";
import useGetBundledGoodsForecast from "hooks/Dashboard/useGetBundledGoodsForecast";

type BundledForecastTableProps = {
    debouncedProductSearch?: string;
    selectedDate?: string;
    startDate?: string;
    activeForecastViewType?: number;
    activeSkuType?: number;
    bundleProductChannelFilter?: string;
    salesChannel?: { [key: string]: any }[] | string[];
    warehouseId?: string;
};

const BundledForecastTable = ({
    debouncedProductSearch,
    selectedDate,
    startDate,
    bundleProductChannelFilter,
    warehouseId,
}: BundledForecastTableProps) => {
    const [chartFilter, setChartFilter] = useState<string>("yearly");
    const [isScrolled, setIsScrolled] = useState<boolean>(false);
    const [cacheData, setCacheData] = useState({});
    const [currYAxisLabel, setCurrYAxisLabel] = useState("");
    const [refreshConfigList, setRefreshConfigList] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const {
        addForecastConfigSuccess,
        fetchPinForecastConfigSuccess,
        fetchUnpinForecastConfigSuccess,
    } = useAppSelector((state) => state.dashboard);

    const { data: gwmTooltip } = useGetBundleDashboardGWMTooltip();

    const {
        data: bundledGoodsData,
        isFetching: bundledGoodsLoading,
        setUpdatedPage: bundledGoodsSetUpdatedPage,
        pagination: bundledGoodsPagination,
    } = useGetBundledGoodsForecast(
        false,
        debouncedProductSearch,
        startDate,
        selectedDate,
        bundleProductChannelFilter,
        refreshConfigList,
        warehouseId
    );

    const forecastTableHeader = [
        {
            title: <div className="flex space-x-1.5 items-center"></div>,
            widthClass: "w-[50px] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">Product</span>

                    <SynthTooltip
                        content={
                            "The unique name identifying this specific item in your inventory."
                        }
                        divId={"product-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Current stock level
                    </span>

                    <SynthTooltip
                        content={
                            "The actual quantity of the product currently available in stock for sale or use at this moment."
                        }
                        divId={"current-stock-level-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Demand forecast
                    </span>

                    <SynthTooltip
                        content={
                            "A projection of how much of this product you’ll need in the future, based on past sales, market trends, and upcoming promotions."
                        }
                        divId={"demand-forecast-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Stock deficit
                    </span>

                    <SynthTooltip
                        content={
                            "The number of units your inventory is short based on forecasted demand. This highlights how much stock needs to be replenished to meet future needs."
                        }
                        divId={"stock-deficit-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Cost forecast
                    </span>

                    <SynthTooltip
                        content={
                            "The predicted total cost to fulfill the forecasted demand, based on the product’s unit cost and the stock deficit."
                        }
                        divId={"cost-forecast-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
        {
            title: (
                <div className="flex space-x-1.5 items-center">
                    <span className="text-xs font-rocGroteskMedium text-slate-700">
                        Replenishment due
                    </span>

                    <SynthTooltip
                        content={
                            "The quantity of stock replenishment needed to mitigate a stockout scenario."
                        }
                        divId={"replenishment-due-column"}
                        icon={<i className="ri-information-line text-slate-600 text-sm"></i>}
                    />
                </div>
            ),
            // widthClass: "w-[21%] !border-r-none",
        },
    ];

    const forecastTableBody = bundledGoodsData?.map((item, idx) => {
        const stockDeficit = warehouseId
            ? item?.forecast?.currentStock - item?.forecast?.forecast
            : item?.productStockDetails?.stockLevel - item?.forecast?.forecast;
        const isPinned = item?.isPinned;

        return [
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <i
                            className={`ri-star-fill ${
                                isPinned ? "text-[#FCD34D]" : "text-[#E2E8F0]"
                            }`}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                isPinned
                                    ? handleUnpinInventory(item?._id)
                                    : handlePinInventory(item?._id);
                            }}
                        ></i>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="">{item?.bundleName || "N/A"}</p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                {formatMoney().format(
                                    warehouseId
                                        ? item?.forecast?.currentStock
                                        : item?.productStockDetails?.stockLevel
                                )}{" "}
                                {item?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                {formatMoney()?.format(item?.forecast?.forecast)}{" "}
                                {item?.productStockDetails?.unitOfMeasurement || ""}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p
                                className={` text-xs ${
                                    String(stockDeficit)[0] === "-" ? "text-r-55" : "text-slate-500"
                                }`}
                            >
                                {String(stockDeficit)[0] === "-" ? (
                                    formatMoney()?.format(String(stockDeficit)?.slice(1)) +
                                    " " +
                                    (item?.productStockDetails?.unitOfMeasurement || "")
                                ) : (
                                    <span className="text-green-500">Sufficent</span>
                                )}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p className="text-slate-500 text-xs ">
                                ${formatMoney()?.format(item?.forecast?.forecastedCost)}
                            </p>
                        </div>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        key={idx}
                        className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600  py-2`}
                    >
                        <div>
                            <p
                                className={` text-xs ${
                                    String(stockDeficit)[0] === "-" ? "text-r-55" : "text-slate-500"
                                }`}
                            >
                                {item?.forecast?.forecastedNeeds === 0 ? (
                                    <span className="text-green-500">Sufficent</span>
                                ) : (
                                    formatMoney()?.format(item?.forecast?.forecastedNeeds) +
                                    " " +
                                    (item?.productStockDetails?.unitOfMeasurement || "")
                                )}
                            </p>
                        </div>
                    </div>
                ),
            },
        ];
    });

    useEffect(() => {
        if (gwmTooltip && chartFilter && currYAxisLabel !== "") {
            setCacheData((prev) => ({
                ...prev,
                [chartFilter]: {
                    ...prev?.[chartFilter],
                    [currYAxisLabel]: gwmTooltip,
                },
            }));
        }
    }, [chartFilter, gwmTooltip, currYAxisLabel]);

    useEffect(() => {
        if (Boolean(addForecastConfigSuccess)) {
            toast.custom((t) => (
                <CustomToast t={t} message={"Configurations saved successfully"} type={"success"} />
            ));
            dispatch(dashboardActions.resetAddForecastConfig());
        }
    }, [dispatch, addForecastConfigSuccess]);

    const handlePinInventory = (inventoryId: string) => {
        dispatch(dashboardActions.pinForecastConfig(inventoryId));
    };

    const handleUnpinInventory = (inventoryId: string) => {
        dispatch(dashboardActions.unpinForecastConfig(inventoryId));
    };

    useEffect(() => {
        if (!!fetchPinForecastConfigSuccess) {
            setRefreshConfigList((prevValue) => !prevValue);
        }
    }, [fetchPinForecastConfigSuccess]);

    useEffect(() => {
        if (!!fetchUnpinForecastConfigSuccess) {
            setRefreshConfigList((prevValue) => !prevValue);
        }
    }, [fetchUnpinForecastConfigSuccess]);

    return (
        <div>
            <div>
                <div className="mt-4">
                    <div className="overflow-x-scroll">
                        <CustomTable
                            tableBody={forecastTableBody}
                            tableHeader={forecastTableHeader}
                            isScrollable={false}
                            isScrolled={isScrolled}
                            isCellBordered={true}
                            scrollWithin={true}
                            isInifiniteScroll={false}
                            isCellSelectable={false}
                            isAllSelectable={false}
                            tableContainerClass="!max-h-[427px]"
                            headerContainerClass="!bg-slate-50"
                            headerItemClass="font-rocGroteskMedium !text-xs !text-slate-700"
                            tableClass="!border !border-slate-200 cursor-pointer"
                            bodyItemClass="!border-slate-200 !cursor-default hover:!bg-slate-50"
                            isLoading={bundledGoodsLoading}
                            renderEmptyTable={() => (
                                <EmptyStateComp
                                    title="No products available"
                                    description={`There are no products available `}
                                    icon={
                                        <i className="ri-archive-2-line text-[48px] text-slate-400"></i>
                                    }
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="flex justify-end mt-5">
                <ResponsivePaginationComponent
                    current={Number(bundledGoodsPagination?.currentPage)}
                    total={Number(bundledGoodsPagination?.noOfPages)}
                    maxWidth={350}
                    onPageChange={(page) => bundledGoodsSetUpdatedPage(page)}
                />
            </div>
        </div>
    );
};

export default BundledForecastTable;
